import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  FileText,
  LayoutList,
  Play,
  Plus,
  RefreshCw,
  Search,
  X,
  XCircle,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Badge } from '@/app/components/ui/badge';
import { Progress } from '@/app/components/ui/progress';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Card, CardContent } from '@/app/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/app/components/ui/pagination';
import {
  useWorkTasks,
  useCreateWorkTask,
  useInstantiateWorkTemplate,
  useWorkTemplate,
  useWorkTemplates,
} from '@/hooks/use-work';
import { useProjects } from '@/hooks/use-projects';
import { ProjectSearchComboBox } from '@/components/shared/project-search-combo-box';
import { usePermissions } from '@/hooks/use-permissions';
import type { ProjectOption } from '@/components/shared/project-search-combo-box';
import type { WorkStepTemplate, WorkTask } from '@/lib/types';

const PAGE_SIZE = 20;

function statusIcon(status: WorkTask['status']) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case 'in_progress':
      return <Play className="h-4 w-4 text-blue-600" />;
    case 'blocked':
      return <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />;
    default:
      return <Circle className="h-4 w-4 text-gray-400" />;
  }
}

function statusLabel(status: WorkTask['status']) {
  switch (status) {
    case 'todo':
      return 'To Do';
    case 'in_progress':
      return 'In Progress';
    case 'completed':
      return 'Completed';
    case 'blocked':
      return 'Blocked';
  }
}

function TaskProgress({ task }: { task: WorkTask }) {
  const percent = Math.round((task.progress || 0) * 100);
  return (
    <div className="flex items-center gap-3 w-full">
      <div className="flex-1 min-w-0">
        <Progress value={percent} className="h-2" />
      </div>
      <span className="text-sm text-gray-600 dark:text-muted-foreground shrink-0 tabular-nums">
        {percent}%
      </span>
      {task.failed_steps > 0 && (
        <Badge variant="destructive" className="shrink-0">
          {task.failed_steps} fail
        </Badge>
      )}
    </div>
  );
}

interface CreateTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTemplate?: WorkStepTemplate | null;
}

function CreateTaskDialog({ open, onOpenChange, initialTemplate = null }: CreateTaskDialogProps) {
  const { hasMinRole } = usePermissions();
  const canManage = hasMinRole('Manager');
  const { data: templatesData } = useWorkTemplates({ page_size: 100, ordering: 'name' }, canManage);
  const createTask = useCreateWorkTask();
  const instantiate = useInstantiateWorkTemplate();

  const [project, setProject] = useState<ProjectOption | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [templateId, setTemplateId] = useState<string>(initialTemplate ? String(initialTemplate.id) : '');

  const templates = templatesData?.results ?? [];

  useEffect(() => {
    if (initialTemplate) {
      setTemplateId(String(initialTemplate.id));
    } else if (!open) {
      setTemplateId('');
    }
  }, [initialTemplate, open]);

  const reset = useCallback(() => {
    setProject(null);
    setTitle('');
    setDescription('');
    setTemplateId('');
  }, []);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) reset();
      onOpenChange(next);
    },
    [onOpenChange, reset]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project) return;

    try {
      if (templateId) {
        await instantiate.mutateAsync({
          id: parseInt(templateId, 10),
          data: {
            project: project.id,
            title: title.trim() || undefined,
          },
        });
      } else {
        await createTask.mutateAsync({
          project: project.id,
          title: title.trim() || 'Untitled task',
          description,
        });
      }
      handleOpenChange(false);
    } catch {
      // error handled by mutation
    }
  };

  const isPending = createTask.isPending || instantiate.isPending;
  const selectedTemplate = templates.find((t) => String(t.id) === templateId);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New Task</DialogTitle>
          <DialogDescription>
            Create a blank task or instantiate from a template on a project.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="task-project">Project *</Label>
            <ProjectSearchComboBox value={project} onChange={setProject} required />
          </div>
          <div>
            <Label htmlFor="task-template">Template (optional)</Label>
            <Select value={templateId || 'blank'} onValueChange={(v) => setTemplateId(v === 'blank' ? '' : v)}>
              <SelectTrigger id="task-template">
                <SelectValue placeholder="Blank task" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="blank">Blank task</SelectItem>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name} {t.items.length > 0 && `(${t.items.length} steps)`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="task-title">Title</Label>
            <Input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={selectedTemplate ? selectedTemplate.name : 'Task title'}
              required={!templateId}
            />
          </div>
          <div>
            <Label htmlFor="task-description">Description</Label>
            <Textarea
              id="task-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function WorkTasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [projectExpanded, setProjectExpanded] = useState<Record<number, boolean>>({});
  const { hasMinRole } = usePermissions();
  const canManage = hasMinRole('Manager');

  const page = parseInt(searchParams.get('page') || '1', 10);
  const projectFilter = searchParams.get('project') || '';
  const statusFilter = searchParams.get('status') || '';
  const searchInput = searchParams.get('q') || '';
  const templateFilter = searchParams.get('template') || '';
  const templateId = templateFilter ? parseInt(templateFilter, 10) : null;
  const [localSearch, setLocalSearch] = useState(searchInput);

  const { data: initialTemplate } = useWorkTemplate(templateId && templateId > 0 ? templateId : 'new');

  useEffect(() => {
    if (canManage && templateId && templateId > 0) {
      setCreateOpen(true);
    }
  }, [canManage, templateId]);

  const handleCreateOpenChange = useCallback(
    (open: boolean) => {
      setCreateOpen(open);
      if (!open && templateFilter) {
        const next = new URLSearchParams(searchParams);
        next.delete('template');
        setSearchParams(next);
      }
    },
    [searchParams, setSearchParams, templateFilter]
  );

  const { data, isLoading, isError, refetch } = useWorkTasks({
    page,
    page_size: PAGE_SIZE,
    q: searchInput,
    project: projectFilter ? parseInt(projectFilter, 10) : '',
    status: statusFilter as WorkTask['status'] | '',
    ordering: '-created_at',
  });
  const { data: projectsData } = useProjects({ page_size: 1000 });
  const projectMap = useMemo(() => {
    const map = new Map<number, string>();
    projectsData?.results.forEach((p) => map.set(p.id, `${p.project_number} — ${p.name}`));
    return map;
  }, [projectsData]);

  const tasks = data?.results ?? [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      });
      if (!updates.page) {
        next.set('page', '1');
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const applySearch = useCallback(() => {
    updateParams({ q: localSearch, page: '1' });
  }, [localSearch, updateParams]);

  const clearFilters = useCallback(() => {
    setLocalSearch('');
    const next = new URLSearchParams();
    setSearchParams(next);
  }, [setSearchParams]);

  const groupedTasks = useMemo(() => {
    if (projectFilter) {
      return [{ projectId: parseInt(projectFilter, 10), tasks }];
    }
    const groups: Record<number, WorkTask[]> = {};
    tasks.forEach((task) => {
      if (!groups[task.project]) groups[task.project] = [];
      groups[task.project].push(task);
    });
    return Object.entries(groups)
      .map(([projectId, items]) => ({ projectId: parseInt(projectId, 10), tasks: items }))
      .sort((a, b) => (projectMap.get(a.projectId) || '').localeCompare(projectMap.get(b.projectId) || ''));
  }, [tasks, projectFilter, projectMap]);

  const toggleProject = (projectId: number) => {
    setProjectExpanded((prev) => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-foreground flex items-center gap-2">
            <LayoutList className="h-6 w-6 text-blue-600" />
            Tasks
          </h1>
          <p className="text-gray-600 dark:text-muted-foreground mt-1">
            Work checklists by project. {totalCount > 0 && `${totalCount} total`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {canManage && (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Task
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <Input
                placeholder="Search tasks..."
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Select value={projectFilter || 'all'} onValueChange={(v) => updateParams({ project: v === 'all' ? '' : v, page: '1' })}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All projects</SelectItem>
                  {projectsData?.results.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.project_number} — {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter || 'all'} onValueChange={(v) => updateParams({ status: v === 'all' ? '' : v, page: '1' })}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="todo">To Do</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              {(projectFilter || statusFilter || searchInput) && (
                <Button variant="ghost" onClick={clearFilters} aria-label="Clear filters">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-red-600 dark:text-red-400">Failed to load tasks.</p>
      ) : tasks.length === 0 ? (
        <div className="text-center py-12 border border-dashed rounded-lg">
          <FileText className="mx-auto h-10 w-10 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-foreground">No tasks yet</h3>
          <p className="text-gray-500 dark:text-muted-foreground mt-1">Create a task to start a checklist run.</p>
          {canManage && (
            <Button className="mt-4" onClick={() => setCreateOpen(true)}>
              Create Task
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {groupedTasks.map(({ projectId, tasks: groupTasks }) => {
              const projectName = projectMap.get(projectId) || `Project ${projectId}`;
              const expanded = projectExpanded[projectId] ?? true;
              return (
                <Card key={projectId}>
                  <CardContent className="p-0">
                    <button
                      type="button"
                      onClick={() => toggleProject(projectId)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900 dark:text-foreground">{projectName}</span>
                      <span className="text-gray-500 dark:text-muted-foreground">
                        {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </span>
                    </button>
                    {expanded && (
                      <div className="divide-y">
                        {groupTasks.map((task) => (
                          <Link
                            key={task.id}
                            to={`/work/tasks/${task.id}`}
                            className="block p-4 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                {statusIcon(task.status)}
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-900 dark:text-foreground truncate">{task.title}</p>
                                  <p className="text-sm text-gray-500 dark:text-muted-foreground truncate">
                                    {task.assigned_to_name ? `Assigned: ${task.assigned_to_name}` : 'Unassigned'}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Badge variant="outline">{statusLabel(task.status)}</Badge>
                              </div>
                              <div className="w-full sm:w-56 lg:w-72">
                                <TaskProgress task={task} />
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => updateParams({ page: String(Math.max(1, page - 1)) })}
                    className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <PaginationItem key={p}>
                    <PaginationLink
                      isActive={p === page}
                      onClick={() => updateParams({ page: String(p) })}
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    onClick={() =>
                      updateParams({ page: String(Math.min(totalPages, page + 1)) })
                    }
                    className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}

      {canManage && (
        <CreateTaskDialog
          open={createOpen}
          onOpenChange={handleCreateOpenChange}
          initialTemplate={initialTemplate || undefined}
        />
      )}
    </div>
  );
}
